import {
  ChannelMessage,
  IParentBridge,
  ChannelNameSpace,
  IChannel,
  CompsNameSpace,
} from '../ChannelMessage';
import { EventEmitter } from 'events';
import WorkerChildBridge from './WorkerChildBridge';
import { stackItem, trackerCb } from '@iv/shared/src/tracker';

const message: EventEmitter = new EventEmitter();
let handle: Function;
let bridge: IParentBridge;
let initCalled = false;

/**
 * 创建互动UI容器
 * @param containerId
 */
function createContainer({ containerId = '' }: IChannel) {
  const template = '<div id="iv-app"></div>';
  const tempElement = document.createElement('div');
  tempElement.innerHTML = template.trim();
  const [uiElement] = tempElement.childNodes;
  const container = containerId ? document.getElementById(containerId) : document.body;
  container.appendChild(uiElement);
  container.style.zIndex = '-1000';
  return container;
}

/**
 * 创建父容器消息通道，一对二，需要处理互动组件引擎消息和互动组件运行消息
 * @param param
 */
export function createParentBridge(param: IChannel) {
  // 初始化互动组件层
  new window.iv.InteractComps();
  // 创建UI容器
  const container = createContainer(param);

  // bridge
  handle = param.init;
  bridge = {
    send(data: ChannelMessage) {
      const { ns, method, data: param, seq } = data;
      if (!seq) {
        // 互动组件引擎消息
        message.emit(`${ns}>${method}`, param);
      }
      // 互动组件运行消息
      window.interactWorker.postMessage({
        type: 'postMessage',
        data,
      });
    },
    show() {
      (container as HTMLElement).style.zIndex = '';
    },
    hide() {
      (container as HTMLElement).style.zIndex = '-1000';
    },
    destroy() {},
  };

  // 接收处理worker消息里面类型是与引擎通信的消息
  const msgHandle = async (data: ChannelMessage) => {
    if (data.ns === CompsNameSpace.tracker && data.method === 'send') {
      trackerCb(data.data as stackItem[], data.isEnd);
      return;
    }

    const { nodeId, ns, method, seq } = data;
    const retMessage: ChannelMessage = { nodeId, ns, method, data: '', seq };
    try {
      retMessage.data = await handle?.(data);
    } catch (err) {
      retMessage.data = err;
      retMessage.type = 'reject';
    }

    bridge.send(retMessage);
  };

  // worker的消息监听
  window.interactWorker.onmessage = (e: any) => {
    const { data } = e;
    switch (data.type) {
      // 互动组件初始化完成
      case 'dataReady':
        bridge.send({
          ns: CompsNameSpace.worker,
          method: 'ready',
          data: data.value,
          nodeId: '',
        });
        break;
      // 互动组件数据更新消息
      case 'dataUpdate':
        bridge.send({
          ns: CompsNameSpace.worker,
          method: 'update',
          data: data.value,
          nodeId: '',
        });
        break;
      // 互动组件消息通道消息
      case 'postMessage':
        msgHandle(data.value);
        break;
      // Vue组件间通信
      case 'vueEmit':
        bridge.send({
          ns: CompsNameSpace.worker,
          method: 'emit',
          data: data.value,
          nodeId: '',
        });
        break;
      default:
        break;
    }
  };

  // worker的错误处理，目前全局上会进行错误捕获和上报，这里暂不做额外处理
  window.interactWorker.onerror = (e: Error) => {
    console.error('interactWorker error:', e);
  };

  return new Promise((resolve) => {
    if (initCalled) {
      resolve(bridge);
    } else {
      bridge.ready = () => {
        resolve(bridge);
      };
    }
  });
}

/**
 * 互动组件引擎消息
 */
export function createChildMainBridge() {
  const listened: { [key: string]: boolean } = {};

  return {
    listen(ns: ChannelNameSpace, event: string, cb: (...args: any[]) => void) {
      const messageEventName = `${ns}>${event}`;
      if (!listened[messageEventName]) {
        listened[messageEventName] = true;
        handle?.({
          nodeId: '',
          ns,
          method: 'on',
          data: { event },
        });
      }
      message.on(messageEventName, cb);
    },
    invoke(data: ChannelMessage) {
      const { ns, method } = data;
      if (ns === ChannelNameSpace.engine && method === 'init') {
        initCalled = true;
        if (bridge?.ready) {
          bridge.ready();
        }
        return;
      }
      if (ns === CompsNameSpace.tracker && method === 'send') {
        trackerCb(data.data as stackItem[], data.isEnd);
        return;
      }
      if (ns === CompsNameSpace.worker) {
        window.interactWorker.postMessage({
          type: method,
          data,
        });
      }
      return handle?.(data);
    },
    destroy() {},
  };
};

/**
 * 互动组件运行消息
 */
export function createChildWorkerBridge() {
  return new WorkerChildBridge();
}
